import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'leading' | 'trailing';
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'leading',
      disabled,
      children,
      className = '',
      type = 'button',
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center font-semibold rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variantStyles = {
      primary:
        'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 focus:ring-primary-500',
      secondary:
        'border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 active:bg-neutral-100 focus:ring-primary-500',
      ghost: 'text-neutral-700 hover:bg-neutral-100 active:bg-neutral-200 focus:ring-primary-500',
      danger:
        'bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-800 focus:ring-danger-500',
    };

    const sizeStyles = {
      sm: 'px-3 py-2 text-sm h-9',
      md: 'px-4 py-2.5 text-base h-11',
      lg: 'px-6 py-3 text-lg h-13',
    };

    const showIcon = loading || icon;
    const iconElement = loading ? (
      <div className="animate-spin rounded-full border-2 border-transparent border-t-current h-4 w-4" />
    ) : (
      icon
    );

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading}
        aria-disabled={disabled || loading}
        className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        {...props}
      >
        {showIcon && iconPosition === 'leading' && (
          <span className={children ? 'mr-2' : ''}>{iconElement}</span>
        )}
        {children}
        {showIcon && iconPosition === 'trailing' && (
          <span className={children ? 'ml-2' : ''}>{iconElement}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';
